import {
  FieldValueExtractor,
  UpdateHandler,
  GlobalPlaceholderResolver,
  CreateSerializer,
  StoreSerializer,
  ContextProvider,
  ValidationHandler
} from './fieldRegistry';
import { MediaFile } from './types';

export const extractTextValue: FieldValueExtractor = (field, cardItem) => {
  const rawValue = cardItem?.[field.name] || '';
  return field.deserializer ? field.deserializer(rawValue) : rawValue;
};

export const extractMediaValue: FieldValueExtractor = (field, cardItem) => {
  return cardItem?.[field.name]?.[0] || null;
};

export const createMediaUpdateHandler = (updateType: string): UpdateHandler => {
  return (field, currentValue, cardItem, cardId, context) => {
    const originalMedia = cardItem?.[field.name]?.[0];
    const currentMedia = currentValue as MediaFile | null;

    const currentId = currentMedia?.id;
    const originalId = originalMedia?.id;

    if (currentId && currentId !== originalId) {
      context.updateFile(cardId, currentMedia, updateType);
    }
  };
};

export const createTextUpdateHandler = (): UpdateHandler => {
  return (field, currentValue, cardItem, cardId, context) => {
    const sanitizedValue = field.sanitizer
      ? field.sanitizer(currentValue as string)
      : currentValue;

    let originalValue = cardItem?.[field.name] || '';
    if (field.deserializer) {
      originalValue = field.deserializer(originalValue);
    }

    if ((currentValue as string).trim() !== originalValue) {
      context.addToBatch(field.name, sanitizedValue);
    }
  };
};

export const resolveMediaPlaceholder = (
  placeholderPath: string
): GlobalPlaceholderResolver => {
  return (field, globalPlaceholders) => {
    const value = placeholderPath
      .split('.')
      .reduce((obj, key) => obj?.[key], globalPlaceholders);
    return value ? { id: value, mediaUrl: '' } : null;
  };
};

export const resolveTextPlaceholder: GlobalPlaceholderResolver = () => null;

export const createTextSerializer = (shouldTrim: boolean): CreateSerializer => {
  return (field, currentValue, data) => {
    const sanitizedValue = field.sanitizer
      ? field.sanitizer(currentValue as string)
      : currentValue;
    const value = shouldTrim
      ? (sanitizedValue as string).trim()
      : (sanitizedValue as string);
    data[field.name] = value;
  };
};

export const createMediaSerializer = (
  targetArrayKey: string
): CreateSerializer => {
  return (field, currentValue, data) => {
    if (!data[targetArrayKey]) {
      data[targetArrayKey] = [];
    }

    if (Array.isArray(currentValue)) {
      currentValue.forEach((item: any) => {
        if (item?.id) {
          data[targetArrayKey].push(item.id);
        }
      });
    } else if (currentValue?.id) {
      data[targetArrayKey].push(currentValue.id);
    }
  };
};

export const createTextStoreSerializer = (): StoreSerializer => {
  return (field, currentValue, newItem) => {
    if (currentValue !== undefined && currentValue !== null) {
      newItem[field.name] = currentValue;
    }
  };
};

export const createMediaStoreSerializer = (): StoreSerializer => {
  return () => {
    // No-op: media fields are populated by backend response
  };
};

export const provideEmptyContext: ContextProvider = () => ({});

const isValuePresent = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

export const validateMediaField: ValidationHandler = (field, value) => {
  if (field.validation?.type === 'required' && !isValuePresent(value)) {
    return {
      valid: false,
      error: `${field.label || field.name} is required`,
      fieldName: field.name
    };
  }
  return null;
};

export const validateTextField: ValidationHandler = () => {
  return null;
};
